/**
 * Component to prompt users to register/login when accessing a shared document.
 * Shows when a document is shared with their email but they don't have an account.
 */

import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'

interface SharedDocumentPromptProps {
  documentId: string
  email: string
  onClose: () => void
}

const SharedDocumentPrompt: React.FC<SharedDocumentPromptProps> = ({
  documentId,
  email,
  onClose,
}) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleRegister = () => {
    // Navigate to register page with email pre-filled and return URL
    const returnUrl = `/editor/${documentId}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
    navigate(`/register?email=${encodeURIComponent(email)}&returnTo=${encodeURIComponent(returnUrl)}`)
  }

  const handleLogin = () => {
    // Navigate to login page with return URL
    const returnUrl = `/editor/${documentId}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
    navigate(`/login?returnTo=${encodeURIComponent(returnUrl)}`, {
      state: { from: { pathname: returnUrl } }
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-center mb-4">
          <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl shadow-lg">
            <svg
              className="w-10 h-10 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-center text-slate-900 dark:text-white mb-2">
          Document Shared With You
        </h2>
        
        <p className="text-center text-slate-600 dark:text-slate-400 mb-6">
          This document has been shared with <strong className="text-slate-900 dark:text-white">{email}</strong>.
          To access it, please create an account or log in if you already have one.
        </p>

        <div className="space-y-3">
          <button
            onClick={handleRegister}
            disabled={isSubmitting}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Create Account
          </button>

          <button
            onClick={handleLogin}
            disabled={isSubmitting}
            className="w-full flex justify-center py-2 px-4 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Log In
          </button>

          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

export default SharedDocumentPrompt

